import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Dropdown, Form } from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { BsDownload, BsJournalBookmark } from 'react-icons/bs';
import PageHeader from 'Utils/PageHeader';
import FetchContainer from 'Utils/FetchContainer';
import { EngineIcon } from 'Utils';
import { getSourceNomenclature } from 'Utils/nomenclature';
import InventorySelector, { InventoryNameModal } from 'Secure/InventorySelector';
import InventoryFieldsAccordion from '../MaskingSources/MaskDialog/InventoryFieldsAccordion';
import '../MaskingSources/MaskDialog/InventoryPage.css';
import {
  listDataSourceInventories,
  createDataSourceInventory,
  updateInventory,
  deleteInventory,
  inventoryContentOf,
  latestInventoryOf,
  getProviders,
  inventoryToSelectedFields,
  selectedFieldsToEncryptInventory,
  startEncryptJob,
} from 'Secure/api';
import {
  discoverTablesToEncryptSelectedFields,
  loadDiscoverOverviewTables,
  searchDiscoverIndexTables,
} from 'Secure/secureDiscoverFields';
import { normalizeSavedTableKeys } from 'Secure/MaskingSources/inventoryMerge';
import { getSelectedFieldsCount, hasFindingEntries } from 'Secure/Utils';
import AppContext from 'Utils/AppContext';
import { getDataSourceNumericId } from 'DataSources/datasourcesApi';
import { exportInventoryFields } from 'Secure/inventoryExport';
import DiscoveryTooltip from 'DiscoveryTooltip';
import { READ_ONLY_TITLE } from 'Auth/permissions';

function mergeEncryptInventoryWithDiscover(inventoryFields, discoverFields) {
  // Re-key bare saved keys from pre-schema-qualification inventories onto their qualified
  // discovery twin (unambiguous matches only) — same migration the masking merge applies.
  inventoryFields = normalizeSavedTableKeys(inventoryFields, discoverFields);
  const tables = new Set([...Object.keys(inventoryFields || {}), ...Object.keys(discoverFields || {})]);
  const result = {};
  for (const table of tables) {
    const invT = (inventoryFields && inventoryFields[table]) || {};
    const disT = (discoverFields && discoverFields[table]) || {};
    const fieldNames = new Set([...Object.keys(invT), ...Object.keys(disT)]);
    result[table] = {};
    for (const field of fieldNames) {
      const inv = invT[field];
      const dis = disT[field];
      const findings =
        dis && hasFindingEntries(dis.findings)
          ? dis.findings
          : inv && inv.findings != null
            ? inv.findings
            : {};
      const checked =
        inv != null ? Boolean(inv.checked) : dis != null ? Boolean(dis.checked) : false;
      const fpeTweak =
        inv != null && inv.fpeTweak != null && String(inv.fpeTweak).trim() !== ''
          ? String(inv.fpeTweak).trim()
          : field;
      const fpeKey =
        inv != null && inv.fpeKey != null && String(inv.fpeKey).trim() !== '' ? String(inv.fpeKey).trim() : '';
      result[table][field] = {
        dataType: (inv && inv.dataType) || (dis && dis.dataType) || 'string',
        findings,
        checked,
        algorithm: 'FPE_UTF8',
        fpeTweak,
        fpeKey,
      };
    }
  }
  return result;
}

function InventoryPage() {
  const history = useHistory();
  const location = useLocation();
  const { id: routeId } = useParams();
  const dataSourceIdParam = Number(routeId);
  const [error, setError] = useState(null);
  const [selectedFields, setSelectedFields] = useState({});
  // Table-index (PeopleSoft-80K) runs: { runId, totalTables, loadedCount } when the
  // discovery overview came from the paged index; null for classic runs. Drives the
  // accordion's server-search fallback when the load was capped.
  const [indexInfo, setIndexInfo] = useState(null);
  const [fetchingData, setFetchingData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inventoryReady, setInventoryReady] = useState(false);
  const [noDiscoverFields, setNoDiscoverFields] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [inventories, setInventories] = useState([]);
  // undefined = not yet resolved (first load picks nav target / latest); null = new unsaved draft.
  const [selectedInventoryId, setSelectedInventoryId] = useState(undefined);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  // FPE key bar removed for this deployment — always send no key so the masking service uses
  // its built-in default. See TODO(FPE key) below. Kept as a const (not state) so the existing
  // selectedFieldsToEncryptInventory(..., defaultFpeKey, ...) call sites keep working unchanged.
  const defaultFpeKey = '';

  const { setNotification, connections, isReadOnly } = useContext(AppContext);
  const source = Number.isFinite(dataSourceIdParam)
    ? (connections || []).find((c) => c?.id === dataSourceIdParam)
    : null;
  const connection = source?.name || '';
  const engine = source ? source.type : '';
  const nomenclature = getSourceNomenclature(engine, source?.datasourceType);
  const dataSourceId = getDataSourceNumericId(source);

  const selectedFieldsRef = useRef(selectedFields);
  const autosaveTimerRef = useRef(null);
  const selectedInventoryIdRef = useRef(selectedInventoryId);
  const selectedProviderRef = useRef(selectedProvider);
  const hydrateSkipAutosaveRef = useRef(true);

  useEffect(() => {
    selectedFieldsRef.current = selectedFields;
  }, [selectedFields]);
  useEffect(() => {
    selectedInventoryIdRef.current = selectedInventoryId;
  }, [selectedInventoryId]);
  useEffect(() => {
    selectedProviderRef.current = selectedProvider;
  }, [selectedProvider]);

  useEffect(() => {
    const loadInventory = async () => {
      if (!dataSourceId) return;
      setFetchingData(true);
      setError(null);
      setNoDiscoverFields(false);
      setInventoryReady(false);
      hydrateSkipAutosaveRef.current = true;
      try {
        const [providerList, inventoryList] = await Promise.all([
          getProviders('ENCRYPT').catch(() => []),
          listDataSourceInventories(dataSourceId).catch(() => []),
        ]);
        setProviders(providerList);
        setInventories(inventoryList);

        // First load: resolve which inventory to edit — navigation target, else the latest,
        // else a new draft. Setting the id re-runs this effect to load its content.
        if (selectedInventoryId === undefined) {
          const navId = location.state?.inventoryId;
          const navInv = navId != null ? inventoryList.find((inv) => inv && inv.id === navId) : null;
          const latest = latestInventoryOf(inventoryList);
          setSelectedInventoryId(navInv ? navInv.id : latest ? latest.id : null);
          return;
        }

        const selectedInventory =
          selectedInventoryId != null
            ? inventoryList.find((inv) => inv && inv.id === selectedInventoryId) || null
            : null;

        // Default to the inventory's saved preference when still available, else the registry
        // default (isDefault), else the first provider.
        const preferredId = selectedInventory?.preferredEncryptProviderId;
        const preferred = preferredId && providerList.find((p) => p.serviceId === preferredId);
        const defaultProv = providerList.find((p) => p.isDefault);
        setSelectedProvider(
          (preferred && preferred.serviceId)
          || (defaultProv && defaultProv.serviceId)
          || (providerList[0]?.serviceId || null)
        );

        const selectedFromInventory = inventoryToSelectedFields(inventoryContentOf(selectedInventory));

        const preferredRunId = location.state && location.state.prevSearchid;
        setIndexInfo(null);
        const overviewTables = await loadDiscoverOverviewTables(dataSourceId, preferredRunId, {
          onWarning: (notification) => setNotification(notification),
          onIndexInfo: (info) => setIndexInfo(info),
        });

        const discoverMap = discoverTablesToEncryptSelectedFields(overviewTables);
        const merged = mergeEncryptInventoryWithDiscover(selectedFromInventory, discoverMap);

        if (Object.keys(merged).length > 0) {
          setSelectedFields(merged);
        } else {
          setSelectedFields({});
          setNoDiscoverFields(true);
        }
      } catch (e) {
        setError(e?.message || 'Unable to load inventory');
      } finally {
        setFetchingData(false);
        setInventoryReady(true);
      }
    };
    loadInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSourceId, location.state && location.state.prevSearchid, selectedInventoryId]);

  // Autosave only updates an EXISTING inventory (partial PUT of content + provider). A new draft
  // stays local until the user clicks Encrypt (which prompts for a name and POSTs) — creating
  // rows implicitly on every toggle would litter the source with unnamed inventories.
  useEffect(() => {
    if (!inventoryReady || !dataSourceId || isReadOnly) return;
    if (hydrateSkipAutosaveRef.current) {
      hydrateSkipAutosaveRef.current = false;
      return;
    }
    if (selectedInventoryId == null) return;
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = setTimeout(async () => {
      autosaveTimerRef.current = null;
      try {
        const content = selectedFieldsToEncryptInventory(
          selectedFieldsRef.current,
          connection,
          defaultFpeKey,
          'ui-user'
        );
        if (selectedInventoryIdRef.current == null) return;
        await updateInventory(selectedInventoryIdRef.current, {
          content,
          preferredEncryptProviderId: selectedProviderRef.current || '',
        });
      } catch (e) {
        setNotification({
          header: 'Inventory save failed',
          body: e?.message || String(e),
        });
      }
    }, 650);
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
    };
  }, [selectedFields, selectedProvider, defaultFpeKey, inventoryReady, dataSourceId, connection,
    selectedInventoryId, setNotification, isReadOnly]);

  // Server-search fallback for capped index-mode loads: fetch tables matching the query
  // from the WHOLE run and stage any that aren't loaded yet (same shape as an initial
  // load). The merge is a view augmentation, not a user edit — skip the autosave it would
  // otherwise trigger; the tables persist the next time the user actually edits something.
  const handleServerSearch = useCallback(async (q) => {
    if (!indexInfo) return;
    const { tables } = await searchDiscoverIndexTables(indexInfo.runId, q);
    if (!tables.length) return;
    const shaped = mergeEncryptInventoryWithDiscover(
      {},
      discoverTablesToEncryptSelectedFields(tables)
    );
    const current = selectedFieldsRef.current || {};
    const newKeys = Object.keys(shaped).filter((tk) => !current[tk]);
    if (!newKeys.length) return;
    hydrateSkipAutosaveRef.current = true;
    setSelectedFields((prev) => {
      const next = { ...prev };
      newKeys.forEach((tk) => {
        if (!next[tk]) next[tk] = shaped[tk];
      });
      return next;
    });
  }, [indexInfo]);

  const serverSearch = useMemo(
    () =>
      indexInfo && indexInfo.totalTables > indexInfo.loadedCount
        ? { totalTables: indexInfo.totalTables, onSearch: handleServerSearch }
        : null,
    [indexInfo, handleServerSearch]
  );

  const refreshInventories = async () => {
    const list = await listDataSourceInventories(dataSourceId).catch(() => []);
    setInventories(list);
    return list;
  };

  const handleRename = async (name) => {
    if (selectedInventoryId == null) return;
    try {
      await updateInventory(selectedInventoryId, { name });
      await refreshInventories();
    } catch (e) {
      setNotification({ header: 'Rename failed', body: e?.message || String(e) });
    }
  };

  const handleDelete = async () => {
    if (selectedInventoryId == null) return;
    try {
      await deleteInventory(selectedInventoryId);
      const list = await refreshInventories();
      const latest = latestInventoryOf(list);
      setSelectedInventoryId(latest ? latest.id : null);
      setNotification({ header: 'Inventory deleted', body: `Deleted encryption inventory for ${connection}` });
    } catch (e) {
      setNotification({ header: 'Delete failed', body: e?.message || String(e) });
    }
  };

  /** Persist the staged fields + provider; POSTs a new inventory when editing a draft. */
  const persistInventory = async (name) => {
    const content = selectedFieldsToEncryptInventory(
      selectedFieldsRef.current,
      connection,
      defaultFpeKey,
      'ui-user'
    );
    if (selectedInventoryId != null) {
      await updateInventory(selectedInventoryId, {
        content,
        preferredEncryptProviderId: selectedProvider || '',
      });
      return selectedInventoryId;
    }
    const created = await createDataSourceInventory(dataSourceId, {
      name,
      content,
      ...(selectedProvider ? { preferredEncryptProviderId: selectedProvider } : {}),
    });
    const newId = created && created.id != null ? created.id : null;
    await refreshInventories();
    if (newId != null) setSelectedInventoryId(newId);
    return newId;
  };

  const doEncrypt = async (name) => {
    setSubmitting(true);
    try {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
        autosaveTimerRef.current = null;
      }
      // Provider routing comes from the inventory's own preferredEncryptProviderId (just
      // persisted above) — no explicit registeredServiceId override, so a stale preference
      // degrades to the registry default instead of failing the start.
      const inventoryId = await persistInventory(name);
      await startEncryptJob({ dataSourceId, inventoryId });
      setNotification({
        header: 'Running encryption',
        body: `Successfully started encryption job for ${connection}`,
      });
      // Job started: land on the Secure Data Landscape, matching the masking flow.
      history.push('/secure');
    } catch (e) {
      setNotification({
        header: 'Encryption failed',
        body: e?.message || String(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Download the on-screen field grid (including unsaved edits) as CSV or JSON.
  const handleExport = (format) => {
    const inv =
      selectedInventoryId != null
        ? (inventories || []).find((i) => i && i.id === selectedInventoryId)
        : null;
    const baseName = (inv && inv.name) || (connection ? `${connection} encryption` : 'inventory');
    const exported = exportInventoryFields(selectedFields, format, baseName);
    if (exported === 0) {
      setNotification({ header: 'Nothing to export', body: 'This inventory has no fields.' });
    }
  };

  const checkedCount = getSelectedFieldsCount(selectedFields);
  // TODO(FPE key): the FPE key bar is intentionally removed for this deployment. With no key
  // supplied, the masking service falls back to its built-in default key (currently a static
  // hardcoded constant — same on every install, visible in source). This is NOT secure for
  // production. Follow-up: give masking-service a per-deployment managed key (datamask.fpe.key /
  // DATAMASK_FPE_KEY, mirroring datamask.unique-id.key and c2.cloud-accounts.encryption-key),
  // then either keep this keyless UI (service holds the key) or reintroduce a managed-key UX.
  // See umbrella TODO.md. Until then canEncrypt only requires a field to be checked; the tweak
  // always resolves (defaults to the field name) so no per-field key/tweak gate is needed.
  const canEncrypt = checkedCount > 0 && !isReadOnly;

  // Reason the Encrypt button is disabled, surfaced via tooltip so the gate isn't silent.
  let encryptDisabledReason = '';
  if (isReadOnly) encryptDisabledReason = READ_ONLY_TITLE;
  else if (submitting) encryptDisabledReason = 'Starting encryption job…';
  else if (!dataSourceId) encryptDisabledReason = 'No data source loaded.';
  else if (checkedCount === 0) encryptDisabledReason = 'Select at least one field to encrypt.';

  return (
    <div className='inv-page-root'>
      <PageHeader
        header='Encryption → Inventory'
        icon={BsJournalBookmark}
        tooltip='Select FPE (FPE_UTF8) columns to encrypt. Saved inventories update automatically; new ones are named on Encrypt.'
        actions={[
          <Dropdown.Item
            key='export-csv'
            disabled={fetchingData}
            onClick={() => handleExport('csv')}
            data-testid='inventory-export-csv'
          >
            <BsDownload className='mr-2' />
            Export CSV
          </Dropdown.Item>,
          <Dropdown.Item
            key='export-json'
            disabled={fetchingData}
            onClick={() => handleExport('json')}
            data-testid='inventory-export-json'
          >
            <BsDownload className='mr-2' />
            Export JSON
          </Dropdown.Item>,
        ]}
      />
      <Card>
        <FetchContainer error={error} fetchingData={fetchingData} minHeight='375px'>
          <div className='inv-page'>
            <div className='inv-title'>
              <div className='d-flex align-items-start justify-content-between'>
                <div>
                  <div className='inv-title-name'>
                    <EngineIcon classes='mr-2' engine={engine} /> {connection}
                  </div>
                  <div className='inv-title-count'>
                    <span className='accent'>{checkedCount}</span> fields selected for encryption
                    {engine ? <span className='inv-title-sub text-uppercase ml-2'>{engine} database</span> : null}
                  </div>
                </div>
                <div className='d-flex flex-column align-items-end' style={{ gap: '0.4rem' }}>
                  <InventorySelector
                    inventories={inventories}
                    selectedId={selectedInventoryId === undefined ? null : selectedInventoryId}
                    onSelect={(id) => setSelectedInventoryId(id)}
                    onNew={isReadOnly ? undefined : () => setSelectedInventoryId(null)}
                    onRename={isReadOnly ? undefined : handleRename}
                    onDelete={isReadOnly ? undefined : handleDelete}
                    disabled={submitting || fetchingData}
                  />
                  {providers && providers.length > 1 && (
                    <Form.Group className='mb-0 d-flex align-items-center'>
                      <Form.Label className='mb-0 mr-2 text-nowrap' style={{ fontWeight: 500 }}>
                        Encryption Provider
                      </Form.Label>
                      <Form.Control
                        as='select'
                        size='sm'
                        data-testid='encrypt-provider-select'
                        value={selectedProvider || ''}
                        onChange={(e) => setSelectedProvider(e.target.value)}
                        style={{ width: 'auto' }}
                      >
                        {providers.map((p) => (
                          <option key={p.serviceId} value={p.serviceId}>
                            {p.vendorDisplayName || p.name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  )}
                </div>
              </div>
              {noDiscoverFields && (
                <Alert variant='warning' className='mt-2 mb-0'>
                  No fields loaded from discovery for this data source. Run a completed discover job first; encryption
                  inventory is built from discovery results only.
                </Alert>
              )}
            </div>
            <div className='inv-scroll'>
              <InventoryFieldsAccordion
                selectedFields={selectedFields}
                setSelectedFields={setSelectedFields}
                isEncryption
                serverSearch={serverSearch}
                nomenclature={nomenclature}
              />
            </div>
            <div className='inv-sticky-bottom'>
                <div className='d-flex justify-content-end'>
                  <Button
                    onClick={() => history.push('/secure/encrypt')}
                    className='mr-2'
                    variant='outline-secondary'
                  >
                    CANCEL
                  </Button>
                  <DiscoveryTooltip
                    text={encryptDisabledReason || 'Run in-place FPE encryption on the checked fields.'}
                    placement='top'
                  >
                    {/* span wrapper so the tooltip still fires while the button is disabled */}
                    <span className='d-inline-block'>
                      <Button
                        disabled={submitting || !dataSourceId || !canEncrypt}
                        style={(submitting || !dataSourceId || !canEncrypt) ? { pointerEvents: 'none' } : undefined}
                        onClick={() => {
                          if (selectedInventoryId == null) {
                            setNameModalOpen(true);
                            return;
                          }
                          doEncrypt();
                        }}
                        variant='primary'
                      >
                        Encrypt
                      </Button>
                    </span>
                  </DiscoveryTooltip>
                </div>
            </div>
          </div>
        </FetchContainer>
      </Card>
      <InventoryNameModal
        show={nameModalOpen}
        title='Name this inventory'
        initialName={connection ? `${connection} encryption` : ''}
        submitLabel='Save & Encrypt'
        onSubmit={(name) => {
          setNameModalOpen(false);
          doEncrypt(name);
        }}
        onCancel={() => setNameModalOpen(false)}
      />
    </div>
  );
}

export default InventoryPage;
